import type { ContentToken } from '@/lib/tokenizeContent'

/**
 * Maps a multi-word phrase surface onto article/story token indices (including
 * non-word spans in the array), covering the clicked anchor token.
 *
 * Soft-matches a small set of head-verb inflections so "looking after" still
 * flashes when the catalog unit is "look after".
 */
export function findCoveringPhraseIndices(
  tokens: ContentToken[],
  anchorTokenIndex: number,
  phraseSurface: string,
): number[] | null {
  const parts = phraseSurface
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length < 2) {
    return null
  }
  if (anchorTokenIndex < 0 || anchorTokenIndex >= tokens.length || !tokens[anchorTokenIndex]?.isWord) {
    return null
  }

  const wordPositions: number[] = []
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i]?.isWord) {
      wordPositions.push(i)
    }
  }

  const anchorWordPos = wordPositions.indexOf(anchorTokenIndex)
  if (anchorWordPos < 0) {
    return null
  }

  for (let start = Math.max(0, anchorWordPos - parts.length + 1); start <= anchorWordPos; start++) {
    const end = start + parts.length - 1
    if (end >= wordPositions.length) {
      continue
    }
    if (anchorWordPos < start || anchorWordPos > end) {
      continue
    }

    let match = true
    for (let k = 0; k < parts.length; k++) {
      const tokenLower = tokens[wordPositions[start + k]!]!.lower
      const expected = parts[k]!
      if (tokenLower === expected) {
        continue
      }
      if (k === 0 && headVerbMatches(tokenLower, expected)) {
        continue
      }
      match = false
      break
    }
    if (match) {
      return wordPositions.slice(start, end + 1)
    }
  }

  return null
}

/** First contiguous match of the phrase in the token stream (explicit multi-word selection). */
export function findFirstPhraseIndices(
  tokens: ContentToken[],
  phraseSurface: string,
): number[] | null {
  const all = findAllPhraseIndices(tokens, phraseSurface)
  return all[0] ?? null
}

/** Every non-overlapping contiguous match of the phrase (left-to-right). */
export function findAllPhraseIndices(
  tokens: ContentToken[],
  phraseSurface: string,
): number[][] {
  const parts = phraseSurface
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length < 2) {
    return []
  }

  const wordPositions: number[] = []
  for (let i = 0; i < tokens.length; i++) {
    if (tokens[i]?.isWord) {
      wordPositions.push(i)
    }
  }

  const matches: number[][] = []
  for (let start = 0; start + parts.length - 1 < wordPositions.length; ) {
    let match = true
    for (let k = 0; k < parts.length; k++) {
      const tokenLower = tokens[wordPositions[start + k]!]!.lower
      const expected = parts[k]!
      if (tokenLower === expected) {
        continue
      }
      if (k === 0 && headVerbMatches(tokenLower, expected)) {
        continue
      }
      match = false
      break
    }
    if (match) {
      matches.push(wordPositions.slice(start, start + parts.length))
      start += parts.length
    } else {
      start += 1
    }
  }
  return matches
}

const HEAD_INFLECTIONS: Record<string, string> = {
  looking: 'look',
  looks: 'look',
  looked: 'look',
  getting: 'get',
  gets: 'get',
  got: 'get',
  putting: 'put',
  puts: 'put',
  taking: 'take',
  takes: 'take',
  took: 'take',
  coming: 'come',
  comes: 'come',
  came: 'come',
  going: 'go',
  goes: 'go',
  went: 'go',
  giving: 'give',
  gives: 'give',
  gave: 'give',
  running: 'run',
  runs: 'run',
  ran: 'run',
  picking: 'pick',
  picks: 'pick',
  picked: 'pick',
  turning: 'turn',
  turns: 'turn',
  turned: 'turn',
  finding: 'find',
  finds: 'find',
  found: 'find',
  making: 'make',
  makes: 'make',
  made: 'make',
  keeping: 'keep',
  keeps: 'keep',
  kept: 'keep',
  standing: 'stand',
  stands: 'stand',
  stood: 'stand',
  sitting: 'sit',
  sits: 'sit',
  sat: 'sit',
  writing: 'write',
  writes: 'write',
  wrote: 'write',
  handing: 'hand',
  hands: 'hand',
  handed: 'hand',
  filling: 'fill',
  fills: 'fill',
  filled: 'fill',
  using: 'use',
  uses: 'use',
  used: 'use',
}

function headVerbMatches(surface: string, expectedLemma: string): boolean {
  return HEAD_INFLECTIONS[surface] === expectedLemma
}
